import { create } from 'zustand';
import type { FocusScope } from '../domains/orgchart/models/FocusScope';
import { DEFAULT_FOCUS_SCOPE } from '../domains/orgchart/models/FocusScope';
import { DEFAULT_ORG_PERSON_NODE_TYPE } from '../domains/orgchart/models/defaults';
import {
  DEFAULT_ORG_WORKLOAD_LENS_SETTINGS,
  type OrgWorkloadLensSettings,
} from '../domains/orgchart/models/OrgWorkloadLens';
import {
  DEFAULT_ORG_SECONDARY_REPORTS_VISIBILITY_MODE,
  type OrgSecondaryReportsVisibilityMode,
} from '../domains/orgchart/models/secondaryReportsVisibility';

export type OrgChartViewpoint = 'none' | 'tenure';

const MAX_FOCUS_HISTORY = 10;
interface OrgChartViewState {
  asOfDate: string | null;
  activeViewpoint: OrgChartViewpoint;
  collapsedNodeIds: string[];
  /** Whether incremental auto-layout is enabled */
  autoLayoutEnabled: boolean;
  nodeSpacing: number;
  verticalSpacing: number;
  /** Visibility mode for secondary reporting edges (dotted / matrix). */
  secondaryReportsVisibilityMode: OrgSecondaryReportsVisibilityMode;
  /** Per-user workload lens window and facet filters. */
  workloadLensSettings: OrgWorkloadLensSettings;
  /** Current focus scope — defaults to 'all' (show everything) */
  focusScope: FocusScope;
  /** History stack for back navigation */
  focusScopeHistory: readonly FocusScope[];
  /** Last selected or used org person card style (node type) for new person creation */
  lastUsedOrgPersonNodeType: string;
  setAsOfDate: (value: string | null) => void;
  setActiveViewpoint: (value: OrgChartViewpoint) => void;
  toggleCollapsed: (nodeId: string) => void;
  clearCollapsed: () => void;
  setCollapsedNodeIds: (nodeIds: string[]) => void;
  resetView: () => void;
  /** Set last-used org person node type (e.g. org_person_photo_inner_left) for new person creation */
  setLastUsedOrgPersonNodeType: (nodeType: string) => void;
  /** Enable or disable incremental auto-layout */
  setAutoLayoutEnabled: (value: boolean) => void;
  setNodeSpacing: (value: number) => void;
  setVerticalSpacing: (value: number) => void;
  setSecondaryReportsVisibilityMode: (value: OrgSecondaryReportsVisibilityMode) => void;
  setWorkloadLensSettings: (patch: Partial<OrgWorkloadLensSettings>) => void;
  resetWorkloadLensSettings: () => void;
  /** Replace focus scope, pushing current to history */
  setFocusScope: (scope: FocusScope) => void;
  /** Reset to 'all' and clear history */
  clearFocusScope: () => void;
  /** Pop one level from history */
  goBackFocusScope: () => void;
  /** Monotonic request token; >0 means there is/was a pending fit request */
  pendingViewportFit: number;
  /**
   * Monotonic completion generation. Unlike `pendingViewportFit`, this is not
   * cleared after a fit, so a parent that misses a fast pending -> clear edge
   * can still prove that the initial viewport became ready.
   */
  viewportFitCompletionCount: number;
  /**
   * True when the pending fit came from an explicit user action rather than a
   * load/seed path. A forced fit must re-derive the framing instead of
   * restoring the persisted viewport — that viewport is wherever the user
   * currently is, so restoring it would make the request a no-op.
   */
  pendingViewportFitForce: boolean;
  requestViewportFit: (options?: { force?: boolean }) => void;
  clearViewportFit: () => void;
  /** Set after a node is expanded — triggers animated viewport adjustment */
  pendingExpandFit: string | null;
  clearExpandFit: () => void;
  /** Timestamp of the last collapse/expand action — gates position animations */
  lastCollapseAt: number;
  /** Set after a node is collapsed — triggers highlight on the parent node */
  pendingCollapseHighlight: string | null;
  clearCollapseHighlight: () => void;
  /** Node ID to center in viewport (e.g. from search result click) */
  pendingCenterNodeId: string | null;
  pendingCenterRequestToken: number;
  requestCenterOnNode: (nodeId: string) => void;
  clearCenterOnNode: (requestToken?: number) => void;

  /** Expand all collapsed branches one level (hydrates CSV if needed). Set by canvas host. */
  expandOneLevelAll: (() => void) | null;
  setExpandOneLevelAll: (fn: (() => void) | null) => void;
  revealMatchedNodeIds: ((nodeIds: string[]) => void) | null;
  setRevealMatchedNodeIds: (fn: ((nodeIds: string[]) => void) | null) => void;

  /** When true, next layout run ignores autoLayoutEnabled (for expand-on-tree-off). */
  forceLayoutOnExpand: boolean;
  setForceLayoutOnExpand: (value: boolean) => void;
}

export const DEFAULT_ORG_NODE_SPACING = 40;
export const DEFAULT_ORG_VERTICAL_SPACING = 120;

export const useOrgChartViewStore = create<OrgChartViewState>((set) => ({
  asOfDate: null,
  activeViewpoint: 'none',
  collapsedNodeIds: [],
  autoLayoutEnabled: true,
  nodeSpacing: DEFAULT_ORG_NODE_SPACING,
  verticalSpacing: DEFAULT_ORG_VERTICAL_SPACING,
  secondaryReportsVisibilityMode: DEFAULT_ORG_SECONDARY_REPORTS_VISIBILITY_MODE,
  workloadLensSettings: DEFAULT_ORG_WORKLOAD_LENS_SETTINGS,
  focusScope: DEFAULT_FOCUS_SCOPE,
  focusScopeHistory: [],
  lastUsedOrgPersonNodeType: DEFAULT_ORG_PERSON_NODE_TYPE,
  pendingViewportFit: 0,
  viewportFitCompletionCount: 0,
  pendingViewportFitForce: false,
  requestViewportFit: (options) => set((state) => ({
    pendingViewportFit: state.pendingViewportFit + 1,
    pendingViewportFitForce: state.pendingViewportFitForce || options?.force === true,
  })),
  clearViewportFit: () => set((state) => ({
    pendingViewportFit: 0,
    pendingViewportFitForce: false,
    viewportFitCompletionCount: state.pendingViewportFit > 0
      ? state.viewportFitCompletionCount + 1
      : state.viewportFitCompletionCount,
  })),
  pendingExpandFit: null,
  clearExpandFit: () => set({ pendingExpandFit: null }),
  lastCollapseAt: 0,
  pendingCollapseHighlight: null,
  clearCollapseHighlight: () => set({ pendingCollapseHighlight: null }),
  pendingCenterNodeId: null,
  pendingCenterRequestToken: 0,
  requestCenterOnNode: (nodeId) => set((state) => ({
    pendingCenterNodeId: nodeId,
    pendingCenterRequestToken: state.pendingCenterRequestToken + 1,
  })),
  clearCenterOnNode: (requestToken) => set((state) =>
    requestToken !== undefined && state.pendingCenterRequestToken !== requestToken
      ? state
      : { pendingCenterNodeId: null }),
  setAsOfDate: (value) => set({ asOfDate: value }),
  setActiveViewpoint: (value) => set({ activeViewpoint: value }),
  setLastUsedOrgPersonNodeType: (nodeType) => set({ lastUsedOrgPersonNodeType: nodeType }),
  toggleCollapsed: (nodeId) => set((state) => {
    const isExpanding = state.collapsedNodeIds.includes(nodeId);
    const now = Date.now();
    if (isExpanding) {
      return {
        collapsedNodeIds: state.collapsedNodeIds.filter((id) => id !== nodeId),
        pendingExpandFit: nodeId,
        lastCollapseAt: now,
      };
    }
    return {
      collapsedNodeIds: [...state.collapsedNodeIds, nodeId],
      pendingCollapseHighlight: nodeId,
      lastCollapseAt: now,
    };
  }),
  clearCollapsed: () => set({ collapsedNodeIds: [] }),
  setCollapsedNodeIds: (nodeIds) => set({ collapsedNodeIds: [...nodeIds] }),
  resetView: () => set({
    asOfDate: null,
    activeViewpoint: 'none',
    collapsedNodeIds: [],
    autoLayoutEnabled: false,
    workloadLensSettings: DEFAULT_ORG_WORKLOAD_LENS_SETTINGS,
    focusScope: DEFAULT_FOCUS_SCOPE,
    focusScopeHistory: [],
    lastUsedOrgPersonNodeType: DEFAULT_ORG_PERSON_NODE_TYPE,
    pendingViewportFit: 0,
    pendingViewportFitForce: false,
    pendingExpandFit: null,
    pendingCollapseHighlight: null,
    pendingCenterNodeId: null,
    pendingCenterRequestToken: 0,
    nodeSpacing: DEFAULT_ORG_NODE_SPACING,
    verticalSpacing: DEFAULT_ORG_VERTICAL_SPACING,
  }),
  setAutoLayoutEnabled: (value) => set({ autoLayoutEnabled: value }),
  setNodeSpacing: (value) => set({ nodeSpacing: value }),
  setVerticalSpacing: (value) => set({ verticalSpacing: value }),
  setSecondaryReportsVisibilityMode: (value) => set({ secondaryReportsVisibilityMode: value }),
  setWorkloadLensSettings: (patch) => set((state) => ({
    workloadLensSettings: {
      ...state.workloadLensSettings,
      ...patch,
    },
  })),
  resetWorkloadLensSettings: () => set({
    workloadLensSettings: DEFAULT_ORG_WORKLOAD_LENS_SETTINGS,
  }),
  setFocusScope: (scope) => set((state) => ({
    focusScope: scope,
    focusScopeHistory: [...state.focusScopeHistory.slice(-MAX_FOCUS_HISTORY + 1), state.focusScope],
  })),
  clearFocusScope: () => set({
    focusScope: DEFAULT_FOCUS_SCOPE,
    focusScopeHistory: [],
  }),
  goBackFocusScope: () => set((state) => {
    const history = [...state.focusScopeHistory];
    const previous = history.pop() ?? DEFAULT_FOCUS_SCOPE;
    return { focusScope: previous, focusScopeHistory: history };
  }),
  expandOneLevelAll: null,
  setExpandOneLevelAll: (fn) => set({ expandOneLevelAll: fn }),
  revealMatchedNodeIds: null,
  setRevealMatchedNodeIds: (fn) => set({ revealMatchedNodeIds: fn }),
  forceLayoutOnExpand: false,
  setForceLayoutOnExpand: (value) => set({ forceLayoutOnExpand: value }),
}));
